import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, TextField, InputAdornment, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, TablePagination, IconButton
} from '@mui/material';
import { Search as SearchIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDateDisplay } from '../../../utils/dateFormatter';
import supportService from '../../../services/supportService';

const STATUSES = ['', 'open', 'in_progress', 'waiting_user', 'waiting_agent', 'resolved', 'closed'];
const PRIORITIES = ['', 'low', 'medium', 'high', 'urgent'];
const STATUS_COLORS = { open: 'info', in_progress: 'warning', waiting_user: 'error', waiting_agent: 'default', resolved: 'success', closed: 'default' };
const PRIORITY_COLORS = { low: 'default', medium: 'info', high: 'warning', urgent: 'error' };

const AdminSupportTicketList = () => {
  const { t } = useTranslation(['support', 'common']);
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: '', priority: '', page: 0, limit: 20 });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page + 1,
        limit: filters.limit,
        ...(filters.search && { search: filters.search }),
        ...(filters.status && { status: filters.status }),
        ...(filters.priority && { priority: filters.priority })
      };
      const res = await supportService.getAdminTickets(params);
      if (res.success) {
        setTickets(res.data?.tickets || res.data?.rows || []);
        setTotal(res.data?.total || res.data?.count || 0);
      }
    } catch {
      setError(t('support:errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {t('support:admin.tickets.title')}
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder={t('support:admin.tickets.search')}
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 0 }))}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('support:admin.tickets.filterStatus')}</InputLabel>
          <Select
            value={filters.status}
            label={t('support:admin.tickets.filterStatus')}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 0 }))}
          >
            {STATUSES.map(s => (
              <MenuItem key={s} value={s}>
                {s ? t(`support:status.${s}`) : t('common:all', 'Tous')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('support:admin.tickets.filterPriority')}</InputLabel>
          <Select
            value={filters.priority}
            label={t('support:admin.tickets.filterPriority')}
            onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value, page: 0 }))}
          >
            {PRIORITIES.map(p => (
              <MenuItem key={p} value={p}>
                {p ? t(`support:priority.${p}`) : t('common:all', 'Tous')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>N°</TableCell>
              <TableCell>{t('support:ticket.subject')}</TableCell>
              <TableCell>{t('support:admin.tickets.user')}</TableCell>
              <TableCell>{t('support:ticket.category')}</TableCell>
              <TableCell>{t('support:admin.tickets.filterStatus')}</TableCell>
              <TableCell>{t('support:admin.tickets.filterPriority')}</TableCell>
              <TableCell>{t('support:admin.tickets.agent')}</TableCell>
              <TableCell>Date</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  {t('support:admin.tickets.noTickets')}
                </TableCell>
              </TableRow>
            ) : (
              tickets.map(ticket => (
                <TableRow key={ticket.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/support/tickets/${ticket.id}`)}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">{ticket.ticketNumber}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{ticket.subject}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ticket.user?.name || ticket.contactEmail || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={t(`support:categories.${ticket.category}`, ticket.category)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={t(`support:status.${ticket.status}`)} size="small" color={STATUS_COLORS[ticket.status]} />
                  </TableCell>
                  <TableCell>
                    <Chip label={t(`support:priority.${ticket.priority}`)} size="small" color={PRIORITY_COLORS[ticket.priority]} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ticket.assignedAgent?.user?.name || t('support:admin.tickets.unassigned')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDateDisplay(ticket.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small"><ViewIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={filters.page}
          onPageChange={(_, page) => setFilters(prev => ({ ...prev, page }))}
          rowsPerPage={filters.limit}
          onRowsPerPageChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 0 }))}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>
    </Box>
  );
};

export default AdminSupportTicketList;
